import React from 'react';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Button from '../Button';

const DialogTitle = withStyles((theme) => ({
  title: {
    width: 393,
    color: '#9E9E9E',
    borderBottom: `1px solid ${theme.palette.lightGrey.light}`,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    '& > h6': {
      fontSize: 15,
    },
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    fontWeight: 'bold',
  },
  closeButton: {
    color: theme.palette.secondary.main,
    padding: 0,
  },
}))(({
  children, classes, onClose, ...other
}) => (
  <MuiDialogTitle
    disableTypography
    className={classes.title}
    {...other}
  >
    <Typography variant="h6" classes={{ root: classes.content }}>{children}</Typography>
    {onClose ? (
      <IconButton aria-label="close" className={classes.closeButton} onClick={onClose} color="primary">
        <CloseIcon />
      </IconButton>
    ) : null}
  </MuiDialogTitle>
));

const DialogActions = withStyles((theme) => ({
  root: {
    flexWrap: 'wrap',
    borderTop: `1px solid ${theme.palette.darkGrey}`,
    padding: theme.spacing(2),
    justifyContent: 'space-evenly',
    '& button': {
      maxWidth: 130,
      boxShadow: 'none',
      fontSize: 15,
      fontWeight: 500,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      '& :not(:first-child)': {
        margin: 0,
      },
    },
  },
}))(({ classes, ...other }) => (
  <MuiDialogActions
    className={classes.root}
    {...other}
  />
));

const DialogContent = withStyles(() => ({
  content: {
    minHeight: 120,
    padding: 0,
  },
}))(({ classes, ...other }) => (
  <MuiDialogContent className={classes.content} {...other} />
));

const Dialog = withStyles((theme) => ({
  primaryButton: {
    color: theme.palette.common.white,
    borderRadius: '15px',
  },
  secondaryButton: {
    borderRadius: '15px',
  },
}))((
  {
    title, children, action, secondaryAction, handleClose, classes,
  }
) => (
  <>
    <DialogTitle onClose={handleClose}>
      {title}
    </DialogTitle>
    <DialogContent>
      {children}
    </DialogContent>
    {!!action && (
      <DialogActions>
        {secondaryAction && (
          <Button
            variant="tertiary"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            className={classes.secondaryButton}
          >
            {secondaryAction.name}
          </Button>
        )}
        <Button
          onClick={action.onClick}
          disabled={action.disabled}
          className={classes.primaryButton}
        >
          {action.name}
        </Button>
      </DialogActions>
    )}
  </>
));

Dialog.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  action: PropTypes.shape({
    name: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
  }),
  secondaryAction: PropTypes.shape({
    name: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
  }),
  handleClose: PropTypes.func,
};

Dialog.defaultProps = {
  action: null,
  secondaryAction: null,
  handleClose: undefined,
};

export default Dialog;
